import { useTutorialContext } from '../../context/Tutorial';
import { DropdownMenu } from '../form';
import { SvgProps } from '../UI/Icon/Icon';
import { SelectViewMode } from './SelectViewMode';
import { STRATEGIES_OPTIONS, useStrategiesContext } from './StrategiesContext';
import { Filter, Mode } from './types';
import { Checkbox } from '../form';
import { ActionButton, Icon } from '../UI';
import React from 'react';
import { useThemeContext } from '../../context/Theme';
import { useTranslation } from 'react-i18next';

const DROPDOWN_ITEMS = ['All', 'Active', 'Inactive', 'Archived'];

const STRATEGY_VIEW_MODES = ['Graph', 'List', 'Grid'] as const;

const MODES: { [t in Mode]: (typeof STRATEGY_VIEW_MODES)[number] } = {
  viewGraph: 'Graph',
  viewGrid: 'Grid',
  viewList: 'List',
};

// Reverse KEY:VALUE pairs of MODES, e.g {Graph: 'viewGraph'}
const MODES_REV = Object.entries(MODES).reduce(
  (prev, [key, val]) => ({ ...prev, [val]: key }),
  {}
);

export const ControlBar = () => {
  const {
    activeStrategies,
    inactiveStrategies,
    filter,
    mode,
    strategiesOption,
    dispatch,
    filterStrategies,
    handleAddWidgetModal,
  } = useStrategiesContext();

  const { t } = useTranslation();

  const { AlignButtonTooltip, StrategiesAlignButtonTooltip } =
    useTutorialContext();

  const { theme } = useThemeContext();

  // TODO add logic for disabled and active situations
  // const isDisabled = 'Merge';

  function renderButtons() {
    return (
      <div className={`flex flex-1 h-full`}>
        {STRATEGIES_OPTIONS.map((item) => {
          const isActive = strategiesOption === item;

          // const isDisabled =
          //   item === 'Align' &&
          //   activeStrategies.length + inactiveStrategies.length < 2;
          // ${isDisabled && 'pointer-events-none'}

          return (
            <div
              key={item}
              className={`
                w-1/${STRATEGIES_OPTIONS.length}
                px-5
                h-full
                flex
                justify-center
                items-center
                font-semibold
                border-l-1
                border-grey-10
                dark:border-grey-70
                ${
                  !isActive &&
                  'text-grey-50 dark:hover:text-white hover:text-black'
                }
                ${
                  isActive &&
                  'dark:bg-grey-80 dark:text-white bg-grey-5 text-black'
                }
              `}
              onClick={() => dispatch({ strategiesOption: item })}
            >
              <span
                className={`
                    flex
                    items-center
                    ${
                      isActive
                        ? 'pointer-events-none'
                        : `cursor-${
                            // isDisabled === item ? 'not-allowed' :
                            'pointer'
                          }`
                    }
                  `}
              >
                <Icon
                  svgName={item.toLocaleLowerCase() as SvgProps['svgName']}
                  className={'text-black dark:text-white'}
                />
                <div className="ml-3 mr-1">
                  {item === 'Strategies' ? 'Tracking' : item}
                </div>
              </span>
              {item === 'Align' &&
                (strategiesOption === 'Align' ? (
                  <AlignButtonTooltip tooltipPopupClassname="w-52" />
                ) : (
                  strategiesOption === 'Strategies' && (
                    <StrategiesAlignButtonTooltip tooltipPopupClassname="w-52" />
                  )
                ))}
            </div>
          );
        })}
        <div
          className={`
            border-l-1
            dark:border-grey-70
            border-grey-10
            h-full
          `}
        />
        {strategiesOption === 'Strategies' && (
          <SelectViewMode mode={mode} onSelect={(mode) => dispatch({ mode })} />
        )}
      </div>
    );
  }

  // @ts-ignore
  return (
    <header className="flex gap-5">
      <div
        className={`
        flex
        justify-between
        border
        border-grey-30
        h-15
        rounded-xl
        shadow-md
        mr-auto
        w-full
        px-5
        dark:bg-grey-90
        dark:border-grey-70
        dark:text-white
      `}
      >
        {/* LEFT */}
        <div className={`flex items-center w-2/5 h-full`}>
          {/* {strategiesOption === 'Merge' && (
          <span className="font-medium text-2xl mr-2">Merge</span>
        )} */}
          <h2
            // ${
            //   strategiesOption === 'Merge' && 'lowercase'
            // }
            className={`font-semibold text-xl capitalize-first`}
          >
            {t('strategies.strategies')}
          </h2>
          {strategiesOption === 'Strategies' && (
            <DropdownMenu
              dropdownClassName={`
              ml-6
              bg-grey-5
              pl-3
              pr-3
              py-1
              rounded-xl
              dark:bg-grey-80
            `}
              dropdownTooltipClassName={`
              bg-white
              border-solid
              border-grey-10
              border-1
              shadow-md
            `}
              dropdownItems={DROPDOWN_ITEMS}
              handleClick={(filter) => filterStrategies(filter as Filter)}
              dropdownArrowClassName="-right-6 text-black"
              showArrow
            >
              {filter}
            </DropdownMenu>
          )}
        </div>

        {/* buttons */}
        <div className={'hidden lg:flex flex-1 h-full'}>{renderButtons()}</div>

        {/* dropdown (small screen) */}
        <div className={'block lg:hidden'}>
          <div
            className={`h-full
          justify-center
          relative
          flex
          items-center
          gap-1
          select-none
          cursor-pointer
        `}
          >
            <DropdownMenu
              hideOnItemClick
              hideOnMouse
              dropdownItems={[
                ...STRATEGIES_OPTIONS.map((item) => {
                  const isActive = strategiesOption === item;
                  const isDisabled =
                    item === 'Align' &&
                    activeStrategies.length + inactiveStrategies.length < 2;

                  return (
                    <div
                      className={`flex items-center gap-3 w-full ${
                        isDisabled && 'pointer-events-none'
                      }`}
                      onClick={() => dispatch({ strategiesOption: item })}
                    >
                      <Icon
                        svgName={
                          item.toLocaleLowerCase() as SvgProps['svgName']
                        }
                        className={'text-black dark:text-white'}
                      />
                      <span className="mr-auto">{item}</span>
                      {isActive && (
                        <Checkbox shape="circle" isCheck={true} size="small" />
                      )}
                    </div>
                  );
                }),
                ...({
                  Strategies: [
                    {
                      item: MODES[mode],
                      dropdownItems: [...STRATEGY_VIEW_MODES],
                      handleClick: (selectedMode) =>
                        dispatch({ mode: MODES_REV[selectedMode] }),
                    },
                  ],
                }[strategiesOption] ?? []),
              ]}
            >
              <Icon svgName={'options'} />
            </DropdownMenu>
          </div>
        </div>
      </div>

      {strategiesOption === 'Compare' && (
        <div
          className={`
                    flex
                    flex-shrink-0
                    items-center
                    justify-center
                    w-16
                    md:w-40
                    h-15
                    border
                    border-grey-30
                    rounded-xl
                    shadow-md
                  `}
        >
          <ActionButton
            className="m-auto"
            text="Add widget"
            size="large"
            hideTextOnMobile={true}
            highlightIcon={true}
            onClick={handleAddWidgetModal}
          />
        </div>
      )}
    </header>
  );
};
